import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@atlas/ui-components'
import { Button, Checkbox } from '@atlas/ui-primitives'
import { formatUtc } from '@/lib/format-utc'
import type { AssetTypeListRow, LookupListRow, MappingVersionDTO } from '@/services/workfront-mappings.service'
import { draftCountsLine } from './DraftBar'
import type { SelectableRows } from './FieldsTable'
import { ListState, type ListStateProps } from './ListState'
import { RowStatusBadge } from './MappingBadges'

type Loadable = Pick<ListStateProps, 'isLoading' | 'isError' | 'onRetry'>

const Header = ({ columns }: { columns: string[] }) => (
	<TableHeader>
		<TableRow>
			{columns.map((column, index) => (
				<TableHead key={`${column}-${index}`}>{column}</TableHead>
			))}
		</TableRow>
	</TableHeader>
)

export interface AssetTypesTableProps extends SelectableRows, Loadable {
	rows: AssetTypeListRow[]
	filtered?: boolean
	assetTypeNames: Record<string, string>
	onEdit: (row: AssetTypeListRow) => void
}

/** The (Classification, Asset Detail) pairs and the asset type each becomes. */
export function AssetTypesTable({
	rows,
	filtered,
	assetTypeNames,
	selected,
	onToggle,
	onEdit,
	...state
}: AssetTypesTableProps) {
	return (
		<ListState
			noun="asset types"
			{...state}
			isEmpty={rows.length === 0}
			emptyTitle={filtered ? 'No asset types match' : 'No asset types mapped yet'}
		>
			<Table aria-label="Asset types">
				<Header columns={['', 'Classification', 'Asset Detail', 'Asset type', 'Status', '']} />
				<TableBody>
					{rows.map(({ key, status, row }) => {
						const pair = `${row.classificationValue} / ${row.assetDetailValue ?? 'none'}`
						return (
							<TableRow key={key}>
								<TableCell>
									<Checkbox
										aria-label={`Select ${pair}`}
										isSelected={selected.has(key)}
										onChange={() => onToggle(key)}
									/>
								</TableCell>
								<TableCell className="font-medium">{row.classificationValue}</TableCell>
								<TableCell>{row.assetDetailValue ?? '—'}</TableCell>
								<TableCell>
									{row.assetTypeCode}
									{assetTypeNames[row.assetTypeCode] ? (
										<span className="text-ds-text-muted"> · {assetTypeNames[row.assetTypeCode]}</span>
									) : null}
								</TableCell>
								<TableCell>
									<RowStatusBadge status={status} />
								</TableCell>
								<TableCell>
									<Button
										size="sm"
										variant="ghost"
										aria-label={`Edit ${pair}`}
										onPress={() => onEdit({ key, status, row })}
									>
										Edit
									</Button>
								</TableCell>
							</TableRow>
						)
					})}
				</TableBody>
			</Table>
		</ListState>
	)
}

export interface LookupsTableProps extends Loadable {
	rows: LookupListRow[]
	onEdit: (row: LookupListRow) => void
}

/** One lookup's rows: the Workfront value, by its name where known, and the ATLAS value. */
export function LookupsTable({ rows, onEdit, ...state }: LookupsTableProps) {
	return (
		<ListState noun="lookup rows" {...state} isEmpty={rows.length === 0} emptyTitle="No rows in this lookup yet">
			<Table aria-label="Lookup rows">
				<Header columns={['Workfront value', 'ATLAS value', 'Status', '']} />
				<TableBody>
					{rows.map((lookupRow) => (
						<TableRow key={lookupRow.key}>
							<TableCell>
								<span className="block font-medium">{lookupRow.sourceLabel ?? lookupRow.row.sourceValue}</span>
								{lookupRow.sourceLabel && lookupRow.sourceLabel !== lookupRow.row.sourceValue ? (
									<span className="text-ds-xs text-ds-text-muted">{lookupRow.row.sourceValue}</span>
								) : null}
							</TableCell>
							<TableCell>{lookupRow.row.targetValue}</TableCell>
							<TableCell>
								<RowStatusBadge status={lookupRow.status} />
							</TableCell>
							<TableCell>
								<Button
									size="sm"
									variant="ghost"
									aria-label={`Edit ${lookupRow.sourceLabel ?? lookupRow.row.sourceValue}`}
									onPress={() => onEdit(lookupRow)}
								>
									Edit
								</Button>
							</TableCell>
						</TableRow>
					))}
				</TableBody>
			</Table>
		</ListState>
	)
}

export interface VersionsTableProps extends Loadable {
	versions: MappingVersionDTO[]
	/** A user's name by id, for who published each version. */
	nameOf: (userId: string) => string
	/** A re-publish is in flight. */
	busy?: boolean
	onRepublish: (version: MappingVersionDTO) => void
}

/** The published versions, newest first; any but the current one can be re-published (a roll back). */
export function VersionsTable({ versions, nameOf, busy, onRepublish, ...state }: VersionsTableProps) {
	return (
		<ListState noun="versions" {...state} isEmpty={versions.length === 0} emptyTitle="Nothing published yet">
			<Table aria-label="Mapping versions">
				<Header columns={['Version', 'Published by', 'When', 'Summary', 'Changes', '']} />
				<TableBody>
					{versions.map((version, index) => (
						<TableRow key={version.version}>
							<TableCell className="font-medium">{version.version}</TableCell>
							<TableCell className="whitespace-nowrap">{nameOf(version.publishedBy)}</TableCell>
							<TableCell className="whitespace-nowrap">{formatUtc(version.publishedAt)}</TableCell>
							<TableCell className="min-w-56">
								{version.summary}
								{version.republishedFrom ? (
									<span className="block text-ds-xs text-ds-text-muted">
										re-published from {version.republishedFrom}
									</span>
								) : null}
							</TableCell>
							<TableCell>{draftCountsLine(version.counts)}</TableCell>
							<TableCell>
								{index === 0 ? (
									<span className="text-ds-xs text-ds-text-muted">current</span>
								) : (
									<Button
										size="sm"
										variant="outline"
										className="whitespace-nowrap"
										isDisabled={busy}
										aria-label={`Re-publish version ${version.version}`}
										onPress={() => onRepublish(version)}
									>
										Re-publish
									</Button>
								)}
							</TableCell>
						</TableRow>
					))}
				</TableBody>
			</Table>
		</ListState>
	)
}
